/**
 * ProgressBar component — Brand Book §2.17
 * 2px hairline track, bg-muted, orange fill, square ends.
 * Width transition on the `--motion-step` token (240ms) / standard easing.
 * Two modes:
 *   - determinate: pass `value` (0–max) → measured fill.
 *   - indeterminate: pass `indeterminate` (no measurable progress) → a sweeping
 *     segment. Reduced-motion collapses it to a static low-opacity fill (see
 *     `.progress-indeterminate` in index.css) so it never reads as a stuck bar.
 * Optional stage indicators with success/orange/muted dots.
 */

import { clsx } from 'clsx';

export interface ProgressStage {
  label: string;
  completed: boolean;
  active?: boolean;
}

export interface ProgressBarProps {
  /** 0–max. Omit when `indeterminate`. */
  value?: number;
  max?: number;
  /** Unmeasured wait — sweeping segment instead of a fixed fill. */
  indeterminate?: boolean;
  stages?: ProgressStage[];
  showPercentage?: boolean;
  statusText?: string;
  className?: string;
}

export function ProgressBar({
  value = 0,
  max = 100,
  indeterminate = false,
  stages,
  showPercentage = false,
  statusText,
  className,
}: ProgressBarProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  return (
    <div className={clsx('w-full', className)}>
      {/* Status text */}
      {statusText && (
        <p className="text-body-sm text-txt-secondary mb-2">{statusText}</p>
      )}

      {/* Track */}
      <div
        className="relative h-0.5 bg-surface-muted w-full overflow-hidden"
        role="progressbar"
        aria-busy={indeterminate || undefined}
        aria-valuenow={indeterminate ? undefined : Math.round(percentage)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {indeterminate ? (
          <div className="progress-indeterminate" />
        ) : (
          <div
            className="absolute inset-y-0 left-0 bg-orange transition-[width] duration-step ease-std"
            style={{ width: `${percentage}%` }}
          />
        )}
      </div>

      {/* Percentage */}
      {showPercentage && !indeterminate && (
        <p className="mt-1 text-caption text-txt-tertiary font-mono">
          {Math.round(percentage)}%
        </p>
      )}

      {/* Stage indicators */}
      {stages && stages.length > 0 && (
        <div className="flex items-center mt-3">
          {stages.map((stage, i) => (
            <div
              key={`${stage.label}-${i}`}
              className={clsx(
                'flex items-center',
                i > 0 && 'flex-1'
              )}
            >
              {/* Connector line */}
              {i > 0 && (
                <div
                  className={clsx(
                    'flex-1 h-px mx-2',
                    stage.completed ? 'bg-success' : 'bg-border'
                  )}
                />
              )}

              {/* Dot + label */}
              <div className="flex items-center gap-1.5">
                <div
                  className={clsx(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    stage.completed
                      ? 'bg-success'
                      : stage.active
                        ? 'bg-orange'
                        : 'bg-surface-muted'
                  )}
                />
                <span
                  className={clsx(
                    'text-caption whitespace-nowrap',
                    stage.completed
                      ? 'text-success'
                      : stage.active
                        ? 'text-orange font-medium'
                        : 'text-txt-tertiary'
                  )}
                >
                  {stage.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}